<template>
  <div>
    <!-- Slide-over Backdrop -->
    <div 
      v-if="isOpen" 
      class="cart-drawer-backdrop" 
      @click="closeCart"
    >
      <!-- Slide-over Panel -->
      <div class="cart-drawer-panel" @click.stop>
        
        <!-- Drawer Header -->
        <div class="cart-drawer-header">
          <div class="header-title-group">
            <div class="header-icon-wrap">
              <icon icon="fa-solid fa-bag-shopping" />
            </div>
            <div>
              <div class="d-flex align-items-center gap-2">
                <h3 class="cart-drawer-title font-serif">Giỏ hàng sách cũ</h3>
                <span class="cart-count-pill" v-if="totalItems > 0">{{ totalItems }}</span>
              </div>
              <span class="cart-drawer-subtitle">Các cuốn sách bạn đã chọn mua</span>
            </div>
          </div>

          <div class="header-actions">
            <button 
              v-if="totalItems > 0" 
              type="button" 
              class="btn-clear-cart" 
              @click="confirmClearCart" 
              title="Xóa toàn bộ giỏ hàng"
            >
              <icon icon="fa-solid fa-trash-can" class="me-1" />
              <span>Xóa tất cả</span>
            </button>
            <button 
              type="button" 
              class="cart-close-btn" 
              @click="closeCart" 
              title="Đóng giỏ hàng (Esc)"
            >
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <!-- Drawer Body -->
        <div class="cart-drawer-body">
          
          <!-- Empty State -->
          <div v-if="totalItems === 0" class="cart-empty-state">
            <div class="empty-icon-wrap">
              <icon icon="fa-solid fa-basket-shopping" />
            </div>
            <h4 class="empty-title font-serif">Giỏ hàng của bạn đang trống</h4>
            <p class="empty-desc">
              Chưa có cuốn sách cũ nào được thêm. Hãy khám phá chợ sách để tìm những cuốn sách ưng ý với giá hời!
            </p>
            <button type="button" class="btn-browse-books" @click="goToOldBooks">
              <icon icon="fa-solid fa-store" class="me-2" />
              <span>Dạo chợ sách cũ</span>
            </button>
          </div>

          <!-- Items Grouped by Booth / Seller -->
          <div v-else class="cart-groups-list">
            <div 
              v-for="group in groupedByBooth" 
              :key="'group-' + group.boothId" 
              class="cart-booth-group"
            >
              <!-- Group Header: Seller / Booth Info -->
              <div class="group-header">
                <div class="group-seller-info">
                  <span class="group-icon"><icon icon="fa-solid fa-store" /></span>
                  <div class="group-text">
                    <span class="group-booth-name" @click="goToBooth(group.boothId)" :title="group.boothName">
                      {{ group.boothName }}
                    </span>
                    <span class="group-seller-name" v-if="group.sellerName">Người bán: {{ group.sellerName }}</span>
                  </div>
                </div>
                <span class="group-type-badge" :class="group.saleType === 1 ? 'badge-combo' : 'badge-single'">
                  {{ group.saleType === 1 ? 'Bán theo Combo' : 'Bán lẻ' }}
                </span>
              </div>

              <!-- Group Items List -->
              <div class="group-items">
                <div 
                  v-for="item in group.items" 
                  :key="item.id" 
                  class="cart-item-card"
                >
                  <!-- Item Image -->
                  <div class="cart-item-cover" @click="goToBooth(item.boothId)">
                    <img :src="getCoverUrl(item.cover)" :alt="item.name" @error="handleImageError" />
                    <span v-if="item.type === 'COMBO' || item.type === 'BOOTH'" class="combo-label-tag">
                      {{ item.type === 'COMBO' ? 'Combo' : 'Trọn gian hàng' }}
                    </span>
                  </div>

                  <!-- Item Details -->
                  <div class="cart-item-details">
                    <div class="d-flex justify-content-between align-items-start gap-2">
                      <h4 class="cart-item-title font-serif" @click="goToBooth(item.boothId)">
                        {{ item.name }}
                      </h4>
                      <button 
                        type="button" 
                        class="btn-remove-item" 
                        @click="removeItem(item.id, item.name)" 
                        title="Xóa cuốn này khỏi giỏ"
                      >
                        <icon icon="fa-solid fa-xmark" />
                      </button>
                    </div>

                    <span class="cart-item-author" v-if="item.author">
                      <icon icon="fa-solid fa-feather-pointed" class="me-1 opacity-75" />{{ item.author }}
                    </span>

                    <!-- Specs: Condition % & Weight -->
                    <div class="cart-item-specs">
                      <span class="spec-chip" v-if="item.perStatus">
                        Mới: <strong>{{ item.perStatus }}%</strong>
                      </span>
                      <span class="spec-chip text-slate" v-if="item.verStatus !== undefined && item.verStatus !== null">
                        {{ getConditionLabel(item.verStatus) }}
                      </span>
                      <span class="spec-chip" v-if="item.weight > 0">
                        <icon icon="fa-solid fa-weight-hanging" class="me-1 opacity-60" />{{ formatWeight(item.weight) }}
                      </span>
                    </div>

                    <!-- Price Row -->
                    <div class="cart-item-price-row">
                      <span class="price-type-text">{{ (item.type === 'COMBO' || item.type === 'BOOTH') ? 'Giá trọn gói:' : 'Giá bán lẻ:' }}</span>
                      <strong class="cart-item-price">{{ formatPrice(item.price) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Drawer Footer -->
        <div v-if="totalItems > 0" class="cart-drawer-footer">
          
          <!-- Summary Box -->
          <div class="cart-summary-box">
            <div class="summary-line">
              <span class="summary-label">Số lượng mục chọn:</span>
              <span class="summary-value"><strong>{{ totalItems }}</strong> mục</span>
            </div>
            <div class="summary-line" v-if="totalWeight > 0">
              <span class="summary-label">Tổng khối lượng ước tính:</span>
              <span class="summary-value">{{ formatWeight(totalWeight) }}</span>
            </div>
            <div class="summary-divider"></div>
            <div class="summary-total-line">
              <span class="total-label">Tổng tạm tính:</span>
              <strong class="total-price-val">{{ formatPrice(totalPrice) }}</strong>
            </div>
          </div>

          <!-- Checkout CTA -->
          <div class="cart-footer-actions">
            <button type="button" class="btn-checkout-primary" @click="openCheckoutModal">
              <span>Tiến hành đặt mua ({{ totalItems }})</span>
              <icon icon="fa-solid fa-arrow-right" class="ms-2" />
            </button>
            <button type="button" class="btn-continue-shopping" @click="closeCart">
              <span>Tiếp tục xem sách</span>
            </button>
          </div>

        </div>

      </div>
    </div>

    <!-- Order / Checkout Modal -->
    <div v-if="showCheckoutModal" class="checkout-modal-overlay" @click="closeCheckoutModal">
      <div class="checkout-modal-box" @click.stop>
        
        <div class="checkout-modal-header">
          <div class="d-flex align-items-center gap-2">
            <div class="checkout-icon-wrap">
              <icon icon="fa-solid fa-truck-fast" />
            </div>
            <div>
              <h3 class="checkout-modal-title font-serif">Xác nhận đặt mua sách cũ</h3>
              <span class="checkout-modal-subtitle">Gửi thông tin liên hệ và địa chỉ nhận hàng</span>
            </div>
          </div>
          <button type="button" class="checkout-close-btn" @click="closeCheckoutModal">
            <icon icon="fa-solid fa-xmark" />
          </button>
        </div>

        <form @submit.prevent="submitOrder">
          <div class="checkout-modal-body">
            
            <!-- Order Review List -->
            <div class="checkout-items-preview">
              <div class="preview-header">
                <span>Danh sách sách đặt mua ({{ totalItems }} mục)</span>
                <strong class="preview-total">{{ formatPrice(totalPrice) }}</strong>
              </div>
              <ul class="preview-list">
                <li v-for="item in cartItems" :key="'co-' + item.id" class="preview-item">
                  <span class="item-name">
                    <icon :icon="item.type === 'COMBO' ? 'fa-solid fa-layer-group' : 'fa-solid fa-book'" class="me-1 opacity-75" />
                    {{ item.name }}
                  </span>
                  <span class="item-price">{{ formatPrice(item.price) }}</span>
                </li>
              </ul>
            </div>

            <!-- Customer Fields -->
            <div class="checkout-field">
              <label class="field-label">Họ và tên người nhận <span class="req">*</span></label>
              <input 
                v-model.trim="orderForm.fullname" 
                type="text" 
                class="field-input" 
                placeholder="Nhập họ và tên của bạn" 
                required 
              />
            </div>

            <div class="checkout-field">
              <label class="field-label">Số điện thoại / Zalo nhận liên hệ <span class="req">*</span></label>
              <input 
                v-model.trim="orderForm.phone" 
                type="tel" 
                class="field-input" 
                placeholder="Ví dụ: 0912 345 678" 
                required 
              />
            </div>

            <div class="checkout-field">
              <label class="field-label">Địa chỉ nhận sách <span class="req">*</span></label>
              <input 
                v-model.trim="orderForm.address" 
                type="text" 
                class="field-input" 
                placeholder="Số nhà, tên đường, phường/xã, quận/huyện, tỉnh/TP" 
                required 
              />
            </div>

            <div class="checkout-field">
              <label class="field-label">Ghi chú cho người bán (Tùy chọn)</label>
              <textarea 
                v-model.trim="orderForm.note" 
                class="field-textarea" 
                rows="2" 
                placeholder="Lời nhắn về thời gian nhận hàng, yêu cầu đóng gói..."
              ></textarea>
            </div>

          </div>

          <div class="checkout-modal-footer">
            <button type="button" class="btn-cancel-checkout" @click="closeCheckoutModal">
              Hủy
            </button>
            <button type="submit" class="btn-submit-order" :disabled="isSubmittingOrder">
              <span v-if="isSubmittingOrder" class="spinner-border spinner-border-sm me-2"></span>
              <icon v-else icon="fa-solid fa-circle-check" class="me-2" />
              {{ isSubmittingOrder ? 'Đang gửi yêu cầu...' : 'Xác nhận Đặt mua' }}
            </button>
          </div>
        </form>

      </div>
    </div>
  </div>
</template>

<script src="./OldBookCartDrawer.js"></script>
<style scoped src="./OldBookCartDrawer.css"></style>
